import { z } from "zod";

const identifierSchema = z.string().min(1);
const pressureIndexSchema = z.number().min(0).max(100);
const coordinatesSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
}).strict();

const regionMetricSchema = z.object({
  id: identifierSchema,
  label: z.string().min(1),
  value: z.number(),
  unit: z.string(),
  delta: z.number(),
  band: z.enum(["stable", "watch", "pressure", "critical"]),
}).strict();

const regionSchema = z.object({
  id: identifierSchema,
  name: z.string().min(1),
  country: z.string().min(1),
  summary: z.string().min(1),
  coordinates: coordinatesSchema,
  boundary: z.array(coordinatesSchema).min(3),
  pressureIndex: pressureIndexSchema,
  populationAffected: z.number().nonnegative(),
  metrics: z.array(regionMetricSchema),
  timeline: z.array(z.object({
    date: z.string().min(1),
    pressureIndex: pressureIndexSchema,
    label: z.string().min(1),
  }).strict()),
}).strict();

const pressurePointSchema = z.object({
  id: identifierSchema,
  regionId: identifierSchema,
  coordinates: coordinatesSchema,
  pressureIndex: pressureIndexSchema,
  pulse: z.boolean(),
  updatedAt: z.string().datetime({ offset: true }),
}).strict();

const regionEventSchema = z.object({
  id: identifierSchema,
  regionId: identifierSchema,
  title: z.string().min(1),
  summary: z.string().min(1),
  source: z.enum(["gdelt", "human-rights", "local-media", "civil-society"]),
  occurredAt: z.string().datetime({ offset: true }),
  pressureDelta: z.number(),
  themes: z.array(z.string().min(1)),
}).strict();

const actorNodeSchema = z.object({
  id: identifierSchema,
  label: z.string().min(1),
  kind: z.enum(["population", "leader", "security", "corporate", "media", "movement"]),
  pressure: pressureIndexSchema,
}).strict();

const actorLinkSchema = z.object({
  source: identifierSchema,
  target: identifierSchema,
  strength: z.number().min(0).max(1),
  relation: z.enum(["repression", "extraction", "resistance", "influence", "exposure"]),
}).strict();

const actorGraphSchema = z.object({
  regionId: identifierSchema,
  nodes: z.array(actorNodeSchema),
  links: z.array(actorLinkSchema),
}).strict();

const leaderSchema = z.object({
  id: identifierSchema,
  name: z.string().min(1),
  country: z.string().min(1),
  role: z.string().min(1),
  pressureIndex: pressureIndexSchema,
  accountabilitySignal: pressureIndexSchema,
  heatTrend: z.array(z.number()),
  summary: z.string().min(1),
  allegations: z.array(z.string().min(1)),
}).strict();

const themeSchema = z.object({
  id: identifierSchema,
  label: z.string().min(1),
  description: z.string().min(1),
  pressureIndex: pressureIndexSchema,
}).strict();

const themeLinkSchema = z.object({
  source: identifierSchema,
  target: identifierSchema,
  weight: z.number().min(0).max(1),
}).strict();

const themeConstellationSchema = z.object({
  themes: z.array(themeSchema),
  links: z.array(themeLinkSchema),
}).strict();

export const anarchyDatasetSchema = z.object({
  generatedAt: z.string().datetime({ offset: true }),
  regions: z.array(regionSchema).min(1),
  pressurePoints: z.array(pressurePointSchema),
  events: z.array(regionEventSchema),
  actorGraphs: z.array(actorGraphSchema),
  leaders: z.array(leaderSchema),
  themeConstellation: themeConstellationSchema,
}).strict();
